import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { notificationService } from '../services';

const getSocketBaseUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  return apiUrl.replace(/\/api\/?$/, '') || 'http://localhost:3000';
};

export interface NotificationItem {
  _id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  actionUrl?: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

interface NotificationsContextType {
  unreadCount: number;
  notifications: NotificationItem[];
  loading: boolean;
  socketConnected: boolean;
  refreshNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const refreshNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        notificationService.getList({ page: 1, limit: 10 }),
        notificationService.getUnreadCount(),
      ]);
      const items = listRes?.items ?? [];
      setNotifications(Array.isArray(items) ? items : []);
      setUnreadCount(typeof countRes === 'number' ? countRes : 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [token]);

  const markAsRead = useCallback(
    async (id: string) => {
      try {
        await notificationService.markAsRead(id);
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
      } catch {
        // ignore
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // ignore
    }
  }, []);

  // Socket connection and real-time notification listeners
  useEffect(() => {
    if (!token) {
      setUnreadCount(0);
      setNotifications([]);
      setSocketConnected(false);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    refreshNotifications();

    const baseUrl = getSocketBaseUrl();
    const socketUrl = `${baseUrl}/notifications`;

    const socket: Socket = io(socketUrl, {
      auth: { token },
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
    });
    socket.on('disconnect', () => {
      setSocketConnected(false);
    });
    socket.on('connect_error', () => {
      setSocketConnected(false);
    });
    socket.on('unreadNotificationsCount', (count: number) => {
      setUnreadCount(count);
    });
    socket.on('newNotification', () => {
      setUnreadCount((c) => c + 1);
      refreshNotifications();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, [token, refreshNotifications]);

  return (
    <NotificationsContext.Provider
      value={{
        unreadCount,
        notifications,
        loading,
        socketConnected,
        refreshNotifications,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
